import { useEffect, useState, useCallback } from 'react';
import { Package, ShoppingCart, Users, DollarSign, Plus, Pencil, Trash2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper function to get status badge color
const getStatusBadgeClass = (status) => {
  const statusColors = {
    delivered: 'bg-emerald-100 text-emerald-800',
    shipped: 'bg-blue-100 text-blue-800',
    processing: 'bg-purple-100 text-purple-800',
    confirmed: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    failed: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800'
  };
  return statusColors[status] || 'bg-gray-100 text-gray-800';
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showProductDialog, setShowProductDialog] = useState(false);
  const [error, setError] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    price: '',
    image: '',
    category: '',
    stock: '',
    featured: false
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/stats`);
      setStats(response.data);
    } catch (err) {
      setError('Failed to load statistics');
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      setProducts(response.data);
    } catch (err) {
      setError('Failed to load products');
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/orders`);
      setOrders(response.data);
    } catch (err) {
      setError('Failed to load orders');
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchProducts();
    fetchOrders();
  }, [fetchStats, fetchProducts, fetchOrders]);

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...productForm,
        price: parseFloat(productForm.price),
        stock: parseInt(productForm.stock)
      };

      if (editingProduct) {
        await axios.put(`${API_URL}/products/${editingProduct.id}`, data);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${API_URL}/products`, data);
        toast.success('Product created successfully');
      }

      setShowProductDialog(false);
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', image: '', category: '', stock: '', featured: false });
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error('Failed to save product');
    }
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      description: product.description,
      price: product.price.toString(),
      image: product.image,
      category: product.category,
      stock: product.stock.toString(),
      featured: product.featured
    });
    setShowProductDialog(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await axios.delete(`${API_URL}/products/${productId}`);
      toast.success('Product deleted successfully');
      fetchProducts();
      fetchStats();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleUpdateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API_URL}/admin/orders/${orderId}?status=${status}`);
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-medical-primary text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-heading font-semibold">Admin Dashboard</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex space-x-4 mb-8 border-b">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === 'overview' ? 'border-b-2 border-medical-primary text-medical-primary' : 'text-gray-600'}`}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === 'products' ? 'border-b-2 border-medical-primary text-medical-primary' : 'text-gray-600'}`}
            data-testid="tab-products"
          >
            Products
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`pb-3 px-1 font-medium transition-colors ${activeTab === 'orders' ? 'border-b-2 border-medical-primary text-medical-primary' : 'text-gray-600'}`}
            data-testid="tab-orders"
          >
            Orders
          </button>
        </div>

        {activeTab === 'overview' && stats && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Products</p>
                    <p className="text-3xl font-semibold mt-2">{stats.total_products}</p>
                  </div>
                  <Package className="w-12 h-12 text-medical-primary opacity-20" />
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Orders</p>
                    <p className="text-3xl font-semibold mt-2">{stats.total_orders}</p>
                  </div>
                  <ShoppingCart className="w-12 h-12 text-medical-primary opacity-20" />
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Users</p>
                    <p className="text-3xl font-semibold mt-2">{stats.total_users}</p>
                  </div>
                  <Users className="w-12 h-12 text-medical-primary opacity-20" />
                </div>
              </div>
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-600 text-sm">Total Revenue</p>
                    <p className="text-3xl font-semibold mt-2">${stats.total_revenue.toFixed(2)}</p>
                  </div>
                  <DollarSign className="w-12 h-12 text-medical-primary opacity-20" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-heading font-semibold">Manage Products</h2>
              <Dialog open={showProductDialog} onOpenChange={setShowProductDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-medical-primary hover:bg-medical-primary/90" data-testid="add-product-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingProduct ? 'Edit Product' : 'Add New Product'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleProductSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Product Name</label>
                      <Input
                        required
                        value={productForm.name}
                        onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                        data-testid="product-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <Textarea
                        required
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        rows={3}
                        data-testid="product-description-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Price ($)</label>
                        <Input
                          type="number"
                          step="0.01"
                          required
                          value={productForm.price}
                          onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                          data-testid="product-price-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Stock</label>
                        <Input
                          type="number"
                          required
                          value={productForm.stock}
                          onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                          data-testid="product-stock-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Category</label>
                      <Input
                        required
                        value={productForm.category}
                        onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                        data-testid="product-category-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Image URL</label>
                      <Input
                        required
                        value={productForm.image}
                        onChange={(e) => setProductForm({ ...productForm, image: e.target.value })}
                        data-testid="product-image-input"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={productForm.featured}
                        onChange={(e) => setProductForm({ ...productForm, featured: e.target.checked })}
                        className="mr-2"
                        data-testid="product-featured-checkbox"
                      />
                      <label className="text-sm font-medium">Featured Product</label>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" className="bg-medical-primary hover:bg-medical-primary/90" data-testid="product-save-button">
                        {editingProduct ? 'Update' : 'Create'} Product
                      </Button>
                      <Button type="button" variant="outline" onClick={() => {
                        setShowProductDialog(false);
                        setEditingProduct(null);
                        setProductForm({ name: '', description: '', price: '', image: '', category: '', stock: '', featured: false });
                      }}>
                        Cancel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Featured</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {products.map((product) => (
                    <tr key={product.id} data-testid={`admin-product-${product.id}`}>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <img src={product.image} alt={product.name} className="w-12 h-12 rounded object-cover mr-3" />
                          <span className="font-medium">{product.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{product.category}</td>
                      <td className="px-6 py-4 text-sm font-medium">${product.price.toFixed(2)}</td>
                      <td className="px-6 py-4 text-sm">{product.stock}</td>
                      <td className="px-6 py-4 text-sm">{product.featured ? '✓' : '-'}</td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button onClick={() => handleEditProduct(product)} className="text-blue-600 hover:text-blue-800" data-testid="edit-product-button">
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeleteProduct(product.id)} className="text-red-600 hover:text-red-800" data-testid="delete-product-button">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'orders' && (
          <div>
            <h2 className="text-2xl font-heading font-semibold mb-6">Manage Orders</h2>
            <div className="space-y-4">
              {orders.map((order) => (
                <div key={order.id} className="bg-white rounded-xl shadow-sm p-6" data-testid={`admin-order-${order.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono font-medium">#{order.id.substring(0, 8)}</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-1">{order.user_email}</p>
                      <p className="text-sm text-gray-500">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>

                    {/* Shipping Address */}
                    {order.shipping_address && (
                      <div className="flex-1 bg-gray-50 rounded-lg p-3 text-sm">
                        <p className="font-medium text-gray-700 mb-1">Ship To:</p>
                        <p className="text-gray-600">
                          {order.shipping_address.full_name}<br />
                          {order.shipping_address.address_line1}<br />
                          {order.shipping_address.address_line2 && <>{order.shipping_address.address_line2}<br /></>}
                          {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.zip_code}
                          {order.shipping_address.phone && <><br />Phone: {order.shipping_address.phone}</>}
                        </p>
                      </div>
                    )}

                    {/* Items & Total */}
                    <div className="flex-1">
                      <p className="font-medium text-gray-700 mb-2">Items ({order.items.length}):</p>
                      <div className="text-sm text-gray-600 space-y-1">
                        {order.items.slice(0, 3).map((item) => (
                          <p key={item.product_id || item.product_name}>{item.quantity}x {item.product_name}</p>
                        ))}
                        {order.items.length > 3 && (
                          <p className="text-gray-400">+{order.items.length - 3} more items</p>
                        )}
                      </div>
                      <p className="text-xl font-semibold text-medical-primary mt-2">${order.total.toFixed(2)}</p>
                    </div>

                    {/* Status Update */}
                    <div className="lg:w-40">
                      <p className="text-sm font-medium text-gray-700 mb-2">Update Status:</p>
                      <Select
                        value={order.status}
                        onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}
                      >
                        <SelectTrigger className="w-full" data-testid="order-status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
              {orders.length === 0 && (
                <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
                  No orders yet
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;